import React from 'react'
import {
  ChevronUp,
  ChevronDown,
  Eye,
  RotateCcw,
  MoreVertical,
  Play,
  Square,
  Loader2,
  X,
} from 'lucide-react'
import type { BatchSummary } from '../../lib/api'
import { StatusBadge } from './StatusBadge'
import { ProgressBar } from './ProgressBar'
import { Pagination } from '../shared/Pagination'

export interface BatchListingTableProps {
  batches: BatchSummary[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onBatchClick: (batchId: number) => void
  onStartMatching?: (batch: BatchSummary) => void
  onStopMatching?: (batch: BatchSummary) => void
  /** Re-run only failed products for this batch. Shown when completed and failedCount > 0. */
  onRetryFailedMatching?: (batch: BatchSummary) => void
  /** When true, all Start buttons are disabled (only one process allowed at a time). */
  isAnyProcessRunning?: boolean
  /** Seconds remaining in cooldown after last batch; Start disabled when > 0. */
  cooldownRemainingSeconds?: number
  /** Live status per batch (from GET /status); used to show Re-run failed vs Start after Stop. */
  statusBatchesByBatchId?: Record<number, { retryFailedOnly?: boolean }>
  sortBy?: SortKey
  sortOrder?: 'asc' | 'desc'
  onSortChange?: (column: SortKey) => void
}

type SortKey =
  | 'id'
  | 'batchName'
  | 'createdAt'
  | 'status'
  | 'totalProducts'
  | 'matchedProducts'
  | 'coverage'

const SortIcon: React.FC<{
  column: SortKey
  activeColumn: SortKey
  order: 'asc' | 'desc'
}> = ({ column, activeColumn, order }) => {
  if (activeColumn !== column) return null
  return order === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
}

export const BatchListingTable: React.FC<BatchListingTableProps> = ({
  batches,
  currentPage,
  totalPages,
  onPageChange,
  onBatchClick,
  onStartMatching,
  onStopMatching,
  onRetryFailedMatching,
  isAnyProcessRunning = false,
  cooldownRemainingSeconds = 0,
  statusBatchesByBatchId = {},
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const [openDropdownId, setOpenDropdownId] = React.useState<number | null>(
    null
  )
  const [dropdownDirection, setDropdownDirection] = React.useState<
    'up' | 'down'
  >('down')

  React.useEffect(() => {
    if (openDropdownId === null) return
    const handleClick = () => setOpenDropdownId(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [openDropdownId])

  const isInCooldown = cooldownRemainingSeconds > 0
  const startDisabled = isAnyProcessRunning || isInCooldown
  const activeSortBy = sortBy ?? 'createdAt'
  const activeSortOrder = sortOrder ?? 'desc'

  const handleSortClick = (column: SortKey) => {
    if (!onSortChange) return
    onSortChange(column)
  }

  return (
    <div className='bg-white rounded-lg shadow border border-gray-200'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead className='bg-gray-50 border-b border-gray-200'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16'>
                No.
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('id')}
              >
                <div className='flex items-center'>
                  Batch ID{' '}
                  <SortIcon
                    column='id'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('batchName')}
              >
                <div className='flex items-center'>
                  Batch Name{' '}
                  <SortIcon
                    column='batchName'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                File Name
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('createdAt')}
              >
                <div className='flex items-center'>
                  Created{' '}
                  <SortIcon
                    column='createdAt'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('status')}
              >
                <div className='flex items-center'>
                  Status{' '}
                  <SortIcon
                    column='status'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]'>
                Progress
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('totalProducts')}
              >
                <div className='flex items-center'>
                  Products{' '}
                  <SortIcon
                    column='totalProducts'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('matchedProducts')}
              >
                <div className='flex items-center'>
                  Matched{' '}
                  <SortIcon
                    column='matchedProducts'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th
                className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer'
                onClick={() => handleSortClick('coverage')}
              >
                <div className='flex items-center'>
                  Coverage{' '}
                  <SortIcon
                    column='coverage'
                    activeColumn={activeSortBy}
                    order={activeSortOrder}
                  />
                </div>
              </th>
              <th className='sticky right-0 z-10 px-3 sm:px-6 py-3 text-center sm:text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-l border-gray-200 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {batches.map((batch, rowIndex) => (
              <tr
                key={batch.id}
                className='group hover:bg-gray-50 cursor-pointer'
                onClick={() => onBatchClick(batch.id)}
              >
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-16'>
                  {rowIndex + 1 + (currentPage - 1) * 50}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                  #{batch.id}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                  <div className='max-w-xs truncate' title={batch.batchName}>
                    {batch.batchName}
                  </div>
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                  {batch.fileName}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                  {new Date(batch.createdAt).toLocaleString()}
                </td>
                <td className='px-6 py-4 whitespace-nowrap'>
                  <StatusBadge
                    status={batch.status}
                    queuePosition={batch.queuePosition ?? null}
                    progress={batch.progress}
                    failedCount={batch.failedCount ?? 0}
                  />
                </td>
                <td className='px-6 py-4 whitespace-nowrap min-w-[160px]'>
                  <ProgressBar
                    progress={batch.progress}
                    status={batch.status}
                  />
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                  {batch.totalProducts}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                  {batch.matchedProducts}
                </td>
                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium'>
                  {(() => {
                    const pct = batch.coverage || 0
                    if (pct > 0 && pct < 0.1) return pct.toFixed(2) + '%'
                    if (pct > 0 && pct < 10) return pct.toFixed(1) + '%'
                    return Math.round(pct) + '%'
                  })()}
                </td>
                <td
                  className={`sticky right-0 px-3 sm:px-6 py-4 whitespace-nowrap text-sm bg-white group-hover:bg-gray-50 border-l border-gray-200 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] text-center ${
                    openDropdownId === batch.id ? 'z-30' : 'z-10'
                  }`}
                >
                  <div className='relative inline-block text-left'>
                    <button
                      type='button'
                      onClick={e => {
                        e.stopPropagation()
                        const nextId =
                          openDropdownId === batch.id ? null : batch.id
                        if (nextId !== null) {
                          const triggerRect = (
                            e.currentTarget as HTMLElement
                          ).getBoundingClientRect()
                          const viewportHeight =
                            window.innerHeight ||
                            document.documentElement.clientHeight ||
                            0
                          const spaceBelow = viewportHeight - triggerRect.bottom
                          const estimatedMenuHeight = 140
                          const padding = 16

                          if (spaceBelow < estimatedMenuHeight + padding) {
                            setDropdownDirection('up')
                          } else {
                            setDropdownDirection('down')
                          }
                        }
                        setOpenDropdownId(nextId)
                      }}
                      className='p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors'
                      title={
                        openDropdownId === batch.id
                          ? 'Close actions'
                          : 'Actions'
                      }
                    >
                      {openDropdownId === batch.id ? (
                        <X size={18} />
                      ) : (
                        <MoreVertical size={18} />
                      )}
                    </button>

                    {openDropdownId === batch.id && (
                      <div
                        className={`absolute right-0 ${
                          dropdownDirection === 'up'
                            ? 'bottom-full mb-2'
                            : 'top-full mt-2'
                        } w-48 bg-white border border-slate-400 rounded-xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] z-50 py-1 overflow-hidden transition-all`}
                      >
                        <button
                          type='button'
                          className='w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors'
                          onClick={e => {
                            e.stopPropagation()
                            setOpenDropdownId(null)
                            onBatchClick(batch.id)
                          }}
                        >
                          <Eye size={16} className='text-blue-500' />
                          View Details
                        </button>

                        {(() => {
                          const liveStatus = statusBatchesByBatchId[batch.id]
                          const isStoppedFromRetryFailed =
                            batch.status === 'stopped' &&
                            liveStatus?.retryFailedOnly === true
                          const isStoppedFromNormalRun =
                            batch.status === 'stopped' &&
                            (liveStatus?.retryFailedOnly === false ||
                              liveStatus?.retryFailedOnly === undefined)
                          const showRerunFailed =
                            onRetryFailedMatching &&
                            (batch.failedCount ?? 0) > 0 &&
                            (batch.status === 'completed' ||
                              isStoppedFromRetryFailed)
                          const showStart =
                            onStartMatching &&
                            batch.status !== 'processing' &&
                            batch.status !== 'completed' &&
                            (batch.status !== 'stopped' ||
                              isStoppedFromNormalRun)

                          let rerunTitle =
                            'Re-run only failed requests for this batch'
                          if (isAnyProcessRunning)
                            rerunTitle =
                              'Another process is running. Only one at a time.'
                          else if (isInCooldown)
                            rerunTitle = `Next run available in ${Math.ceil(cooldownRemainingSeconds / 60)} min`

                          let startTitle = 'Start matching'
                          if (isAnyProcessRunning)
                            startTitle =
                              'Another process is running. Only one at a time.'
                          else if (isInCooldown)
                            startTitle = `Next run available in ${Math.ceil(cooldownRemainingSeconds / 60)} min`

                          return (
                            <>
                              {showStart && (
                                <button
                                  type='button'
                                  disabled={startDisabled}
                                  className='w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                  onClick={e => {
                                    e.stopPropagation()
                                    setOpenDropdownId(null)
                                    onStartMatching?.(batch)
                                  }}
                                  title={startTitle}
                                >
                                  <Play
                                    size={16}
                                    className='text-emerald-500'
                                  />
                                  Start Matching
                                </button>
                              )}
                              {showRerunFailed && (
                                <button
                                  type='button'
                                  disabled={startDisabled}
                                  className='w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-amber-50 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                  onClick={e => {
                                    e.stopPropagation()
                                    setOpenDropdownId(null)
                                    onRetryFailedMatching?.(batch)
                                  }}
                                  title={rerunTitle}
                                >
                                  <RotateCcw
                                    size={16}
                                    className='text-amber-500'
                                  />
                                  Re-run Failed
                                </button>
                              )}
                            </>
                          )
                        })()}

                        {batch.status === 'processing' && (
                          <>
                            <div className='w-full px-4 py-2.5 text-left text-sm font-medium text-slate-500 flex items-center gap-2 cursor-default bg-slate-50/50'>
                              <Loader2
                                size={16}
                                className='animate-spin text-primary'
                              />
                              Running…
                            </div>
                            {onStopMatching && (
                              <button
                                type='button'
                                className='w-full px-4 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors'
                                onClick={e => {
                                  e.stopPropagation()
                                  setOpenDropdownId(null)
                                  onStopMatching(batch)
                                }}
                              >
                                <Square
                                  size={14}
                                  fill='currentColor'
                                  className='text-red-500 ml-[1px]'
                                />
                                Stop Processing
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr>
                <td
                  colSpan={11}
                  className='px-6 py-10 text-center text-sm text-gray-500'
                >
                  No batches found. Upload a file to create your first batch.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
        <div className='text-sm text-gray-600 font-medium'>
          Showing page <span className='text-slate-900'>{currentPage}</span> of{' '}
          <span className='text-slate-900'>{Math.max(totalPages, 1)}</span>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default BatchListingTable
